import React, { useEffect, useState, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInviteAuth } from './Invite/InviteAuthContext';
import InviteAuthModal from '../components/InviteAuthModal';
import './LandingPage.css';
import { 
    LayoutDashboard, Store, ShoppingBag, Sparkles, Mail, Building, Search, Users, Headphones 
} from 'lucide-react';

interface Petal {
    id: number;
    left: string;
    size: string;
    animationDuration: string;
    animationDelay: string;
    type: number;
}

const LandingPage: React.FC = () => {
    const { isAuthenticated, user, openProfileModal } = useAuth();
    const { inviteUser } = useInviteAuth();
    const history = useHistory();
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
    const [petals, setPetals] = useState<Petal[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedLocations, setSelectedLocations] = useState<string[]>(["All India"]);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
        return localStorage.getItem('shadigram_language') || "English";
    });

    const LOCATION_OPTIONS = ["All India", "Jaipur", "Delhi", "Mumbai", "Udaipur", "Goa", "Bengaluru", "Chandigarh", "Lucknow", "Hyderabad"];

    const handleLocationToggle = (loc: string) => {
        if (loc === "All India") {
            setSelectedLocations(["All India"]);
            return;
        }
        setSelectedLocations(prev => {
            const withoutAll = prev.filter(l => l !== "All India");
            if (withoutAll.includes(loc)) {
                const next = withoutAll.filter(l => l !== loc);
                return next.length === 0 ? ["All India"] : next;
            } else {
                return [...withoutAll, loc];
            }
        });
    };

    const isLocationSelected = (loc: string) => selectedLocations.includes(loc);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    try {
                        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                        const data = await response.json();
                        const city = data.address.city || data.address.town || data.address.village || data.address.state || "All India";
                        if (LOCATION_OPTIONS.includes(city)) {
                            setSelectedLocations([city]);
                        } else {
                            setSelectedLocations(["All India"]);
                        }
                    } catch (err) {
                        setSelectedLocations(["All India"]);
                    }
                },
                () => setSelectedLocations(["All India"])
            );
        }
    }, []);

    const handleLanguageChange = (lang: string) => {
        setSelectedLanguage(lang);
        localStorage.setItem('shadigram_language', lang);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            history.push(`/marketplace?search=${encodeURIComponent(searchQuery.trim())}`);
        } else {
            history.push('/marketplace');
        }
    };

    useEffect(() => {
        // Generate falling petals
        const newPetals: Petal[] = Array.from({ length: 15 }).map((_, i) => ({
            id: i,
            left: `${Math.random() * 100}%`,
            size: `${Math.random() * 15 + 10}px`,
            animationDuration: `${Math.random() * 10 + 10}s`,
            animationDelay: `${Math.random() * 10}s`,
            type: Math.floor(Math.random() * 4)
        }));
        setPetals(newPetals);
    }, []);

    const handleInviteClick = (e: React.MouseEvent) => {
        if (!inviteUser) {
            e.preventDefault();
            setIsAuthModalOpen(true);
        }
    };

    const dashboardPath = useMemo(() => {
        if (!isAuthenticated) return '/login';
        if (user?.role === 'superadmin') return '/superadmin/dashboard';
        if (user?.role === 'admin') return '/admin/dashboard';
        if (user?.role === 'photographer') return '/photographer/dashboard';
        if (user?.role === 'agency') return '/agency/';
        return '/dashboard';
    }, [user, isAuthenticated]);

    const invitePath = useMemo(() => {
        if (inviteUser) return '/invite/dashboard';
        return '/invite/login';
    }, [inviteUser]);

    return (
        <div className="landing-page">
            {/* Reusable SVG Gold Gradient for Premium Icons */}
            <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }} aria-hidden="true">
                <defs>
                    <linearGradient id="gold-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#C5A059" />
                        <stop offset="30%" stopColor="#E5C483" />
                        <stop offset="70%" stopColor="#A8844A" />
                        <stop offset="100%" stopColor="#8A662D" />
                    </linearGradient>
                </defs>
            </svg>

            {/* ==========================================
                1. DESKTOP ONLY LANDING PAGE
                ========================================== */}
            <div className="desktop-only-landing">
                {/* Shadigram Hub Gateway Hero Section */}
                <section className="hero-hub-gateway">
                    <div className="petals-overlay">
                        {petals.map((petal) => (
                            <div
                                key={petal.id}
                                className={`petal petal-type-${petal.type}`}
                                style={{
                                    left: petal.left,
                                    width: petal.size,
                                    height: petal.size,
                                    animationDuration: petal.animationDuration,
                                    animationDelay: petal.animationDelay,
                                }}
                            />
                        ))}
                    </div>

                    <div className="hub-dashboard-layout">
                        {/* Central Hero Headline & Description */}
                        <div className="hub-center-hero">
                            <h2 className="hub-pre-title">Welcome to the Ultimate Wedding Hub</h2>
                            <h1 className="hub-title-main">Shadi Gram Control Center</h1>
                            <p className="hub-tagline-sub">
                                The centralized marketplace, directory, planning suite, and invitation manager.
                            </p>
                        </div>

                        {/* Central Global Search Bar Container */}
                        <div className="hub-search-container">
                            <form onSubmit={handleSearchSubmit} className="hub-search-form">
                                <div className="hub-search-input-wrapper">
                                    <Search className="hub-search-icon" size={20} />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search venues, lehengas, planning tools, photography..."
                                        className="hub-search-input"
                                    />
                                </div>
                                <button type="submit" className="hub-search-button">
                                    Search
                                </button>
                            </form>
                        </div>

                        {/* Launcher Grid - 2 Big Cards Per Row with Functionality Backgrounds */}
                        <div className="hub-launcher-grid">
                            {/* Tile 1: E-Invitations Studio with Visual Side Bar */}
                            <div 
                                className="launcher-card card-with-sidebar glass-panel"
                                onClick={(e) => {
                                    handleInviteClick(e);
                                    if (inviteUser) {
                                        history.push('/invite/dashboard');
                                    }
                                }}
                            >
                                {/* Left Side Bar: Image Column with overlay badge */}
                                <div className="card-sidebar-media-col">
                                    <div className="card-sidebar-img" style={{ backgroundImage: `url('/e_invitation_bg.jpg')` }} />
                                    <div className="card-sidebar-badge">
                                        <span className="badge-pulse-dot" /> Live RSVP
                                    </div>
                                </div>

                                {/* Right Side: Content and Actions */}
                                <div className="card-sidebar-content-col">
                                    <div className="card-top-row">
                                        <div className="card-badges-group">
                                            <span className="card-badge badge-gold">Free Tool</span>
                                            <span className="card-badge badge-emerald">WhatsApp & Telegram</span>
                                        </div>
                                        <div className="launcher-card-icon icon-compact">
                                            <Mail size={22} />
                                        </div>
                                    </div>
                                    <div className="launcher-card-body">
                                        <h3 className="launcher-card-title">E-Invitations & Smart RSVP</h3>
                                        <p className="launcher-card-desc">
                                            Design breathtaking digital wedding cards, attach video trailers & photo memories, craft AI-personalized messages, and dispatch automatic 12-hour countdown reminders on WhatsApp.
                                        </p>
                                        <div className="launcher-card-tags">
                                            <span>✨ AI Cards</span>
                                            <span>💬 WhatsApp RSVP</span>
                                            <span>🎬 Photo & Video</span>
                                            <span>⏰ 12h Reminders</span>
                                        </div>
                                    </div>
                                    <div className="launcher-card-action-hint">
                                        <span>Create E-Invitations</span>
                                        <span className="action-arrow">→</span>
                                    </div>
                                </div>
                            </div>

                            {/* Tile 2: My Wedding Planner */}
                            <div 
                                className="launcher-card card-with-sidebar glass-panel"
                                onClick={() => history.push(dashboardPath)}
                            >
                                {/* Left Side Bar: Image Column with overlay badge */}
                                <div className="card-sidebar-media-col">
                                    <div className="card-sidebar-img" style={{ backgroundImage: `url('/wedding_planner_bg.jpg')` }} />
                                    <div className="card-sidebar-badge" style={{ color: '#1e40af', borderColor: 'rgba(59, 130, 246, 0.4)' }}>
                                        <span className="badge-pulse-dot" style={{ background: '#3b82f6', boxShadow: '0 0 5px #3b82f6' }} /> Smart Suite
                                    </div>
                                </div>

                                {/* Right Side: Content and Actions */}
                                <div className="card-sidebar-content-col">
                                    <div className="card-top-row">
                                        <div className="card-badges-group">
                                            <span className="card-badge badge-blue">Protected Suite</span>
                                            <span className="card-badge badge-emerald">Budget & Guests</span>
                                        </div>
                                        <div className="launcher-card-icon icon-compact">
                                            <LayoutDashboard size={22} />
                                        </div>
                                    </div>
                                    <div className="launcher-card-body">
                                        <h3 className="launcher-card-title">My Wedding Planner</h3>
                                        <p className="launcher-card-desc">
                                            All-in-one planning suite: manage multi-family guestlists, live RSVP counts, ceremony schedules, food counters, and dynamic budget sheets in real-time.
                                        </p>
                                        <div className="launcher-card-tags">
                                            <span>📊 Seating & Food</span>
                                            <span>💰 Smart Budget</span>
                                            <span>✅ Ceremony Tasks</span>
                                        </div>
                                    </div>
                                    <div className="launcher-card-action-hint">
                                        <span>Launch Planning Suite</span>
                                        <span className="action-arrow">→</span>
                                    </div>
                                </div>
                            </div>

                            {/* Tile 3: Agency Portal */}
                            <div 
                                className="launcher-card card-bg-agency glass-panel"
                                onClick={() => history.push('/agency')}
                            >
                                <div className="card-bg-media" />
                                <div className="card-bg-overlay" />
                                <div className="card-top-row">
                                    <div className="launcher-card-icon">
                                        <Building size={30} />
                                    </div>
                                    <div className="card-badge badge-purple">B2B Portal</div>
                                </div>
                                <div className="launcher-card-body">
                                    <h3 className="launcher-card-title">Agency & Planner Portal</h3>
                                    <p className="launcher-card-desc">
                                        Dedicated multi-tenant control center for professional wedding planners and event management companies. Manage unlimited client weddings from one console.
                                    </p>
                                    <div className="launcher-card-tags">
                                        <span>🏢 Multi-Wedding Hub</span>
                                        <span>👥 Client Admin Access</span>
                                        <span>🚀 B2B Network</span>
                                    </div>
                                </div>
                                <div className="launcher-card-action-hint">
                                    <span>Enter Agency Portal</span>
                                    <span className="action-arrow">→</span>
                                </div>
                            </div>

                            {/* Tile 4: Shadigram Shop */}
                            <div 
                                className="launcher-card card-bg-shop glass-panel"
                                onClick={() => history.push('/marketplace?tab=products')}
                            >
                                <div className="card-bg-media" />
                                <div className="card-bg-overlay" />
                                <div className="card-top-row">
                                    <div className="launcher-card-icon">
                                        <ShoppingBag size={30} />
                                    </div>
                                    <div className="card-badge badge-coral">Curated Shop</div>
                                </div>
                                <div className="launcher-card-body">
                                    <h3 className="launcher-card-title">Shadigram Luxury Shop</h3>
                                    <p className="launcher-card-desc">
                                        Shop bespoke physical wedding cards, luxury mithai boxes, personalized gift hampers, handcrafted wedding favors, and memorable return gifts.
                                    </p>
                                    <div className="launcher-card-tags">
                                        <span>🎁 Return Favors</span>
                                        <span>💌 Bespoke Stationary</span>
                                        <span>✨ Luxury Boxes</span>
                                    </div>
                                </div>
                                <div className="launcher-card-action-hint">
                                    <span>Explore Shop</span>
                                    <span className="action-arrow">→</span>
                                </div>
                            </div>

                            {/* Tile 5: Vendor Services */}
                            <div 
                                className="launcher-card card-bg-vendors glass-panel"
                                onClick={() => history.push('/marketplace?tab=stores')}
                            >
                                <div className="card-bg-media" />
                                <div className="card-bg-overlay" />
                                <div className="card-top-row">
                                    <div className="launcher-card-icon">
                                        <Store size={30} />
                                    </div>
                                    <div className="card-badge badge-gold">Verified Pros</div>
                                </div>
                                <div className="launcher-card-body">
                                    <h3 className="launcher-card-title">Curated Vendor Services</h3>
                                    <p className="launcher-card-desc">
                                        Discover and book verified luxury palace venues, high-end wedding photographers, royal caterers, makeup artists, and live band performers across India.
                                    </p>
                                    <div className="launcher-card-tags">
                                        <span>🏰 Royal Venues</span>
                                        <span>📸 Cinematic Photo/Video</span>
                                        <span>🍽️ Palace Catering</span>
                                    </div>
                                </div>
                                <div className="launcher-card-action-hint">
                                    <span>Browse Top Vendors</span>
                                    <span className="action-arrow">→</span>
                                </div>
                            </div>

                            {/* Tile 6: Luxury Rentals */}
                            <div 
                                className="launcher-card card-bg-rentals glass-panel"
                                onClick={() => history.push('/marketplace?tab=rentals')}
                            >
                                <div className="card-bg-media" />
                                <div className="card-bg-overlay" />
                                <div className="card-top-row">
                                    <div className="launcher-card-icon">
                                        <Sparkles size={30} />
                                    </div>
                                    <div className="card-badge badge-amber">Exclusive Couture</div>
                                </div>
                                <div className="launcher-card-body">
                                    <h3 className="launcher-card-title">Luxury Bridal & Groom Rentals</h3>
                                    <p className="launcher-card-desc">
                                        Rent exquisite designer bridal lehengas, hand-embroidered royal sherwanis, and authentic heritage gold & Kundan jewelry sets for your special days.
                                    </p>
                                    <div className="launcher-card-tags">
                                        <span>👗 Bridal Lehengas</span>
                                        <span>👑 Royal Sherwanis</span>
                                        <span>💎 Kundan Jewelry</span>
                                    </div>
                                </div>
                                <div className="launcher-card-action-hint">
                                    <span>Browse Luxury Rentals</span>
                                    <span className="action-arrow">→</span>
                                </div>
                            </div>

                            {/* Tile 7: Be the Guest */}
                            <div 
                                className="launcher-card card-bg-guest glass-panel"
                                onClick={() => history.push('/be-a-guest')}
                            >
                                <div className="card-bg-media" />
                                <div className="card-bg-overlay" />
                                <div className="card-top-row">
                                    <div className="launcher-card-icon">
                                        <Users size={30} />
                                    </div>
                                    <div className="card-badge badge-green">Cultural Immersion</div>
                                </div>
                                <div className="launcher-card-body">
                                    <h3 className="launcher-card-title">Be the Guest: Cultural Experience</h3>
                                    <p className="launcher-card-desc">
                                        Experience authentic Indian weddings as an international or curious guest. Discover customs, traditional dress codes, rituals, and join real festivities.
                                    </p>
                                    <div className="launcher-card-tags">
                                        <span>🎉 Cultural Experience</span>
                                        <span>🥁 Baraat Traditions</span>
                                        <span>🥻 Dress Code Guide</span>
                                    </div>
                                </div>
                                <div className="launcher-card-action-hint">
                                    <span>Join Cultural Wedding</span>
                                    <span className="action-arrow">→</span>
                                </div>
                            </div>

                            {/* Tile 8: Contact To Expert */}
                            <div 
                                className="launcher-card card-bg-expert glass-panel"
                                onClick={() => window.open('https://wa.me/917999782728?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20consult%20a%20wedding%20expert.', '_blank', 'noopener,noreferrer')}
                            >
                                <div className="card-bg-media" />
                                <div className="card-bg-overlay" />
                                <div className="card-top-row">
                                    <div className="launcher-card-icon">
                                        <Headphones size={30} />
                                    </div>
                                    <div className="card-badge badge-emerald">24/7 Concierge</div>
                                </div>
                                <div className="launcher-card-body">
                                    <h3 className="launcher-card-title">Talk to a Wedding Expert</h3>
                                    <p className="launcher-card-desc">
                                        Get instant 1-on-1 personalized guidance from certified wedding specialists on WhatsApp for venue selection, budgeting, invitations, or vendor negotiations.
                                    </p>
                                    <div className="launcher-card-tags">
                                        <span>💬 Instant WhatsApp</span>
                                        <span>👰 1-on-1 Planning</span>
                                        <span>⚡ Free Guidance</span>
                                    </div>
                                </div>
                                <div className="launcher-card-action-hint">
                                    <span>Chat with Specialist</span>
                                    <span className="action-arrow">→</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Explanatory SEO Sections */}
                <section className="features-section">
                    <div className="container">
                        <div className="section-header">
                            <h2 className="section-title">Why Choose Shadi Gram?</h2>
                            <p className="section-subtitle">Intelligent tools designed to make wedding planning effortless</p>
                        </div>

                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-icon">💎</div>
                                <h3>Multi-Wedding Support</h3>
                                <p>Manage multiple weddings from a single, powerful dashboard with segregated access.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">📱</div>
                                <h3>Guest Experience</h3>
                                <p>Personalized timelines, interactive maps, and real-time updates for every guest.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">📊</div>
                                <h3>Budget Tracking</h3>
                                <p>Real-time expense monitoring and budget allocation to keep everything on track.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">🖼️</div>
                                <h3>Shared Gallery</h3>
                                <p>Collect every memory from guests in a high-quality shared cloud gallery.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Free Invitation Tool CTA Showcase */}
                <section className="invite-cta-section">
                    <div className="container">
                        <div className="invite-cta-card">
                            <div className="invite-cta-content">
                                <div className="invite-cta-badge">🆓 Free Invitation Tool</div>
                                <h2>Send Beautiful Omnichannel Invites</h2>
                                <p>Reach your guests wherever they are. Build campaign batches and dispatch personalized templates instantly over WhatsApp, SMS, interactive IVR calls, or Telegram bots.</p>
                                <div className="invite-cta-features">
                                    <span>💒 Wedding</span>
                                    <span>🎂 Birthday</span>
                                    <span>🏠 Housewarming</span>
                                    <span>💍 Anniversary</span>
                                    <span>🏢 Corporate</span>
                                </div>
                                <div className="invite-cta-actions">
                                    <Link to="/invite/register" className="btn-invite-primary">
                                        Send Free Invites Now ✨
                                    </Link>
                                    <Link to="/invite/login" className="btn-invite-secondary">
                                        Already have an account? Log In
                                    </Link>
                                </div>
                            </div>
                            <div className="invite-cta-visual">
                                <div className="phone-mockup">
                                    <div className="phone-screen">
                                        <div className="mock-message">
                                            🎊 <strong>You're Invited!</strong> 🎊<br/>
                                            Raj & Priya's Wedding Reception<br/>
                                            📅 15th Dec 2026 · 📍 Jaipur<br/>
                                            <br/>
                                            Please RSVP here:<br/>
                                            shadigram.com/invite/rsvp/...
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Teaser */}
                <section className="pricing-teaser-section">
                    <div className="container">
                        <div className="section-header">
                            <h2 className="section-title">Simple, Honest Pricing</h2>
                            <p className="section-subtitle">Pick a plan that matches your celebration — upgrade anytime.</p>
                        </div>

                        <div className="pricing-teaser-toggle">
                            <span className="save-teaser-badge">🎉 Save 20% with Annual Plans</span>
                        </div>

                        <div className="pricing-teaser-grid">
                            <div className="teaser-card">
                                <div className="teaser-plan-name">🌸 Basic</div>
                                <div className="teaser-price"><span className="teaser-currency">₹</span>300<span className="teaser-period">/mo</span></div>
                                <ul className="teaser-features">
                                    <li>✦ 10 GB Storage</li>
                                    <li>✦ 100 Guest Invitations</li>
                                    <li>✦ Standard Support</li>
                                </ul>
                                <Link to="/pricing" className="teaser-cta teaser-cta--ghost">Get Started</Link>
                            </div>

                            <div className="teaser-card teaser-card--popular">
                                <div className="teaser-popular-badge">Most Popular</div>
                                <div className="teaser-plan-name">💍 Silver</div>
                                <div className="teaser-price"><span className="teaser-currency">₹</span>999<span className="teaser-period">/mo</span></div>
                                <ul className="teaser-features">
                                    <li>✦ 100 GB Storage</li>
                                    <li>✦ 250 Guest Invitations</li>
                                    <li>✦ RSVP Tracking</li>
                                    <li>✦ Priority Support</li>
                                </ul>
                                <Link to="/pricing" className="teaser-cta teaser-cta--solid">Choose Silver</Link>
                            </div>

                            <div className="teaser-card teaser-card--gold-dark">
                                <div className="teaser-plan-name">👑 Gold</div>
                                <div className="teaser-price"><span className="teaser-currency">₹</span>1,999<span className="teaser-period">/mo</span></div>
                                <ul className="teaser-features">
                                    <li>✦ Unlimited Storage</li>
                                    <li>✦ Unlimited Guests</li>
                                    <li>✦ AI RSVP Agent</li>
                                    <li>✦ Custom Domain</li>
                                </ul>
                                <Link to="/pricing" className="teaser-cta teaser-cta--gold-dark">Go Gold ✨</Link>
                            </div>
                        </div>

                        <div className="pricing-teaser-footer">
                            <Link to="/pricing" className="see-all-plans-btn">See Full Plan Details →</Link>
                            <p className="pricing-trust">🔒 Secure via UPI &amp; Razorpay &nbsp;·&nbsp; 7-day money-back guarantee</p>
                        </div>
                    </div>
                </section>

                {/* B2B Agency Plans Teaser */}
                <section className="agency-teaser-section">
                    <div className="container">
                        <div className="section-header">
                            <div className="badge" style={{ marginBottom: '1rem', background: '#e2e8f0', color: '#475569' }}>For Event Professionals</div>
                            <h2 className="section-title">Scale Your Wedding Business</h2>
                            <p className="section-subtitle">Specialized B2B plans for event management agencies. Manage multiple client weddings from a single powerful dashboard.</p>
                        </div>

                        <div className="pricing-teaser-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
                            <div className="teaser-card">
                                <div className="teaser-plan-name">🏢 Agency Starter</div>
                                <div className="teaser-price"><span className="teaser-currency">₹</span>Free</div>
                                <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '1.5rem', marginTop: '-0.5rem' }}>Perfect for new wedding planners</p>
                                <ul className="teaser-features">
                                    <li>✦ Manage up to 3 Weddings</li>
                                    <li>✦ Centralized Dashboard</li>
                                    <li>✦ Invite Client Admins</li>
                                    <li>✦ Standard Support</li>
                                </ul>
                                <Link to="/agency/register" className="teaser-cta teaser-cta--ghost">Partner for Free</Link>
                            </div>

                            <div className="teaser-card teaser-card--popular" style={{ transform: 'scale(1.05)' }}>
                                <div className="teaser-popular-badge">Professional Choice</div>
                                <div className="teaser-plan-name">🚀 Agency Pro</div>
                                <div className="teaser-price"><span className="teaser-currency">₹</span>4,999<span className="teaser-period">/mo</span></div>
                                <p style={{ color: '#f1f5f9', fontSize: '0.9rem', marginBottom: '1.5rem', marginTop: '-0.5rem' }}>For established event management companies</p>
                                <ul className="teaser-features">
                                    <li>✦ Manage Unlimited Weddings</li>
                                    <li>✦ Centralized Dashboard</li>
                                    <li>✦ Priority Account Manager</li>
                                    <li>✦ Vendor Network Access</li>
                                </ul>
                                <Link to="/agency/register" className="teaser-cta teaser-cta--solid">Apply for Pro</Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Free Trial CTA */}
                <section className="free-trial-section">
                    <div className="container">
                        <div className="free-trial-card">
                            <div className="free-trial-icon">🎁</div>
                            <h2 className="free-trial-title">Try Shadi Gram <span className="highlight">Free!</span></h2>
                            <p className="free-trial-subtitle">
                                Get free trial credentials instantly. Contact us on WhatsApp — we'd love to help you plan the perfect wedding!
                            </p>
                            <div className="whatsapp-buttons">
                                <a
                                    href="https://wa.me/917999782728?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20get%20free%20trial%20credentials."
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="whatsapp-btn"
                                >
                                    <svg viewBox="0 0 448 512" fill="currentColor"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg>
                                    79997 82728
                                </a>
                                <a
                                    href="https://wa.me/919826017177?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20get%20free%20trial%20credentials."
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="whatsapp-btn"
                                >
                                    <svg viewBox="0 0 448 512" fill="currentColor"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg>
                                    98260 17177
                                </a>
                            </div>

                            <p className="free-trial-note">💬 Message us on WhatsApp for instant access</p>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="landing-footer">
                    <div className="container">
                        <div className="footer-content">
                            <div className="footer-brand">
                                <img src="/shadigram-logo-bold.png" alt="Shadi Gram logo" className="footer-logo" />
                            </div>
                            <p>&copy; {new Date().getFullYear()} Shadi Gram — Shadi Wali App. All rights reserved.</p>
                        </div>
                    </div>
                </footer>
            </div>

            {/* ==========================================
                2. MOBILE ONLY WEDDING SUPER APP DASHBOARD
                ========================================== */}
            <div className="mobile-only-dashboard">
                {/* Compact Sticky Header */}
                <header className="mobile-header">
                    <div className="mobile-header-left">
                        <img src="/shadigram-logo-bold.png" alt="Shadi Gram" className="mobile-header-logo" onClick={() => history.push('/')} />
                    </div>
                    
                    {/* Animated Sparkle Indicator */}
                    <div className="mobile-header-animation" title="ShadiGram Sparkle">
                        <span className="sparkle-star s1">✨</span>
                        <span className="header-anim-text">ShadiGram</span>
                        <span className="pulse-heart-mini">💖</span>
                        <span className="sparkle-star s2">✨</span>
                    </div>

                    <div className="mobile-header-actions">
                        <button className="header-action-btn" onClick={() => history.push(dashboardPath)} title="Notifications">
                            🔔
                        </button>

                        {/* Profile dropdown */}
                        <div className={`mobile-profile-dropdown${mobileProfileOpen ? ' open' : ''}`}>
                            <button
                                className="header-action-btn"
                                onClick={() => setMobileProfileOpen(prev => !prev)}
                                title="Profile"
                            >
                                👤
                            </button>

                            {mobileProfileOpen && (
                                <div className="mobile-profile-menu">
                                    {/* Overlay to close on outside tap */}
                                    <div className="mobile-profile-overlay" onClick={() => setMobileProfileOpen(false)} />

                                    <div className="mobile-profile-menu-inner">
                                        <div className="mobile-profile-menu-header">
                                            <span 
                                                className="mobile-profile-menu-title"
                                                onClick={() => {
                                                    if (isAuthenticated) {
                                                        setMobileProfileOpen(false);
                                                        openProfileModal();
                                                    }
                                                }}
                                                style={isAuthenticated ? { cursor: 'pointer', textDecoration: 'underline' } : undefined}
                                                title={isAuthenticated ? "Click to view full profile" : undefined}
                                            >
                                                {isAuthenticated ? (user?.name || 'My Account') : 'Guest'}
                                            </span>
                                            <button className="mobile-profile-menu-close" onClick={() => setMobileProfileOpen(false)}>✕</button>
                                        </div>

                                        <div className="mobile-profile-menu-divider" />

                                        {/* Language Row */}
                                        <div className="mobile-profile-menu-row">
                                            <span className="mobile-profile-menu-row-label">🌐 Language</span>
                                            <select
                                                value={selectedLanguage}
                                                onChange={(e) => { handleLanguageChange(e.target.value); setMobileProfileOpen(false); }}
                                                className="mobile-profile-lang-select"
                                            >
                                                <option value="English">English</option>
                                                <option value="Hindi">Hindi</option>
                                                <option value="Punjabi">Punjabi</option>
                                                <option value="Gujarati">Gujarati</option>
                                                <option value="Bengali">Bengali</option>
                                            </select>
                                        </div>

                                        <div className="mobile-profile-menu-divider" />

                                        {/* Dashboard Link */}
                                        <button className="mobile-profile-menu-item" onClick={() => { history.push(dashboardPath); setMobileProfileOpen(false); }}>
                                            📊 {isAuthenticated ? 'My Dashboard' : 'Login'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Compact Hero Area & High Search Section */}
                <div className="mobile-search-section">
                    <form onSubmit={handleSearchSubmit} className="mobile-search-form">
                        <div className="mobile-search-wrapper">
                            <Search className="mobile-search-icon" size={18} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search venues, photographers, lehengas, invitations..."
                                className="mobile-search-input"
                            />
                            <button type="button" className="voice-search-btn" title="Voice Search (Stub)">
                                🎙️
                            </button>
                        </div>
                    </form>
                </div>

                {/* Personalized Widget (Only if logged in) */}
                {isAuthenticated && (
                    <div className="mobile-personalized-widget glass-panel">
                        <div className="widget-header">
                            <span 
                                className="widget-title" 
                                onClick={openProfileModal}
                                style={{ cursor: 'pointer' }}
                                title="Click to view full profile"
                            >
                                ✨ Welcome, <span style={{ textDecoration: 'underline' }}>{user?.name || 'Planner'}</span>
                            </span>
                            <span className="widget-countdown">⏳ 154 Days to Wedding</span>
                        </div>
                        <div className="widget-stats-grid">
                            <div className="stat-card" onClick={() => history.push('/admin/tasks')}>
                                <span className="stat-value">12</span>
                                <span className="stat-label">Tasks Left</span>
                            </div>
                            <div className="stat-card" onClick={() => history.push('/invite/dashboard')}>
                                <span className="stat-value">85%</span>
                                <span className="stat-label">RSVP Done</span>
                            </div>
                            <div className="stat-card" onClick={() => history.push('/budget')}>
                                <span className="stat-value">62%</span>
                                <span className="stat-label">Budget Used</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Grid (4x2) */}
                <div className="mobile-quick-actions">
                    <div className="mobile-quick-actions-row">
                        <div className="quick-action-item" onClick={(e) => {
                            handleInviteClick(e);
                            if (inviteUser) history.push('/invite/dashboard');
                        }}>
                            <div className="quick-action-circle bg-gold-1">💌</div>
                            <span className="quick-action-label">e-Invitation</span>
                        </div>
                        <div className="quick-action-item" onClick={() => history.push('/admin/wedding-planner')}>
                            <div className="quick-action-circle bg-gold-2">🔮</div>
                            <span className="quick-action-label">Match Kundli</span>
                        </div>
                        <div className="quick-action-item" onClick={() => history.push('/admin/wedding-planner')}>
                            <div className="quick-action-circle bg-gold-3">🪐</div>
                            <span className="quick-action-label">Shubh Mahurat</span>
                        </div>
                        <div className="quick-action-item" onClick={() => history.push('/admin/ceremonies')}>
                            <div className="quick-action-circle bg-gold-4">🪔</div>
                            <span className="quick-action-label">Shadi Rasme</span>
                        </div>
                    </div>
                    <div className="mobile-quick-actions-row">
                        <div className="quick-action-item" onClick={() => history.push('/marketplace')}>
                            <div className="quick-action-circle bg-gold-1">🛍️</div>
                            <span className="quick-action-label">Shopping</span>
                        </div>
                        <div className="quick-action-item" onClick={() => history.push('/marketplace?tab=rentals')}>
                            <div className="quick-action-circle bg-gold-2">👗</div>
                            <span className="quick-action-label">Rental</span>
                        </div>
                        <div className="quick-action-item" onClick={() => history.push('/be-a-guest')}>
                            <div className="quick-action-circle bg-gold-3">🤵</div>
                            <span className="quick-action-label">Be the Guest</span>
                        </div>
                        <div className="quick-action-item" onClick={() => window.open('https://wa.me/917999782728?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20consult%20a%20wedding%20expert.', '_blank', 'noopener,noreferrer')}>
                            <div className="quick-action-circle bg-gold-4">📞</div>
                            <span className="quick-action-label">Contact to Expert</span>
                        </div>
                    </div>
                </div>

                {/* Promotional Swipeable Carousel */}
                <div className="mobile-promo-section">
                    <h3 className="section-title-compact">💎 Featured Offers</h3>
                    <div className="mobile-carousel scroll-snap-carousel">
                        <div className="promo-card promo-banner-pink" onClick={(e) => {
                            handleInviteClick(e);
                            if (inviteUser) history.push('/invite/dashboard');
                        }}>
                            <div className="promo-content">
                                <span className="promo-badge">Free Tool</span>
                                <h4>Free Invitation Builder</h4>
                                <p>Omnichannel RSVP campaigns on WhatsApp & SMS</p>
                            </div>
                            <span className="promo-action">Create Free Card →</span>
                        </div>
                        <div className="promo-card promo-banner-gold" onClick={() => history.push('/pricing')}>
                            <div className="promo-content">
                                <span className="promo-badge">Premium</span>
                                <h4>Luxury Ad-free Sites</h4>
                                <p>Elegant custom templates and online registries</p>
                            </div>
                            <span className="promo-action">View Templates →</span>
                        </div>
                        <div className="promo-card promo-banner-dark" onClick={() => history.push('/marketplace?tab=stores')}>
                            <div className="promo-content">
                                <span className="promo-badge">Exclusive</span>
                                <h4>Partner Venue Discounts</h4>
                                <p>Save up to 15% on booking heritage locations</p>
                            </div>
                            <span className="promo-action">Claim Discount →</span>
                        </div>
                    </div>
                </div>


                {/* Core Features Horizontal Scroll Carousel */}
                <div className="mobile-features-section">
                    <h3 className="section-title-compact">🛠️ Our Core Services</h3>
                    <div className="mobile-carousel scroll-snap-carousel">
                        <div className="feature-carousel-card glass-panel" onClick={() => history.push('/agency')}>
                            <div className="feature-card-header-comp">
                                <span className="feature-card-icon-big">🏢</span>
                                <h4>Agency Portal</h4>
                            </div>
                            <p>Manage client accounts, track commissions, and sync team tasks.</p>
                            <button className="feature-card-btn">Open Portal</button>
                        </div>
                        <div className="feature-carousel-card glass-panel" onClick={() => history.push(dashboardPath)}>
                            <div className="feature-card-header-comp">
                                <span className="feature-card-icon-big">📅</span>
                                <h4>Wedding Planner</h4>
                            </div>
                            <p>Interactive guest lists, check lists, and live budget tracking sheets.</p>
                            <button className="feature-card-btn">Launch Suite</button>
                        </div>
                        <div className="feature-carousel-card glass-panel" onClick={() => history.push('/pricing')}>
                            <div className="feature-card-header-comp">
                                <span className="feature-card-icon-big">💎</span>
                                <h4>ShadiGram Plans</h4>
                            </div>
                            <p>Upgrade for unlimited guest invites, RSVPs, and 24/7 expert support.</p>
                            <button className="feature-card-btn">View Plans</button>
                        </div>

                    </div>
                </div>

                {/* Personalized Widget: Continue Planning (if logged in) */}
                {isAuthenticated && (
                    <div className="mobile-recent-section">
                        <h3 className="section-title-compact">⏰ Continue Planning</h3>
                        <div className="recent-grid">
                            <div className="recent-item" onClick={() => history.push('/admin/vendors')}>
                                <span className="recent-icon">🏪</span>
                                <div className="recent-details">
                                    <h5>Shortlisted Venues</h5>
                                    <p>Resume review of 3 saved properties</p>
                                </div>
                                <span className="recent-arrow">→</span>
                            </div>
                            <div className="recent-item" onClick={() => history.push('/invite/dashboard')}>
                                <span className="recent-icon">💌</span>
                                <div className="recent-details">
                                    <h5>Active RSVP Campaigns</h5>
                                    <p>Review metrics of your Sangeet invite cards</p>
                                </div>
                                <span className="recent-arrow">→</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Trending Categories with Location Multi-Select Filter */}
                <div className="mobile-trending-section">
                    <div className="trending-section-header">
                        <h3 className="section-title-compact">🔥 Trending on Shadigram</h3>
                        <span className="trending-location-label">📍 Filter by location</span>
                    </div>

                    {/* Multi-select location pill chips */}
                    <div className="location-pill-bar">
                        {LOCATION_OPTIONS.map(loc => (
                            <button
                                key={loc}
                                className={`location-pill${isLocationSelected(loc) ? ' location-pill--active' : ''}`}
                                onClick={() => handleLocationToggle(loc)}
                                aria-pressed={isLocationSelected(loc)}
                            >
                                {loc === "All India" ? "🇮🇳" : "📍"} {loc}
                                {isLocationSelected(loc) && loc !== "All India" && <span className="pill-check">✓</span>}
                            </button>
                        ))}
                    </div>

                    {/* Selected location context line */}
                    <div className="trending-location-context">
                        {selectedLocations.includes("All India")
                            ? "Showing top trending across all of India"
                            : `Trending in: ${selectedLocations.join(" · ")}`
                        }
                    </div>

                    <div className="mobile-carousel scroll-snap-carousel">
                        <div className="trending-card" onClick={() => history.push(
                            `/marketplace?tab=stores${!selectedLocations.includes('All India') ? `&location=${selectedLocations.join(',')}` : ''}`
                        )}>
                            <img src="https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&q=80&w=400" alt="Venues" className="trending-img" />
                            <div className="trending-title">
                                Trending Venues
                                {!selectedLocations.includes('All India') && (
                                    <span className="trending-loc-badge">{selectedLocations[0]}{selectedLocations.length > 1 ? ` +${selectedLocations.length - 1}` : ''}</span>
                                )}
                            </div>
                        </div>
                        <div className="trending-card" onClick={(e) => { handleInviteClick(e); if (inviteUser) history.push('/invite/dashboard'); }}>
                            <img src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?auto=format&fit=crop&q=80&w=400" alt="Invitations" className="trending-img" />
                            <div className="trending-title">
                                Popular Invitations
                                {!selectedLocations.includes('All India') && (
                                    <span className="trending-loc-badge">{selectedLocations[0]}{selectedLocations.length > 1 ? ` +${selectedLocations.length - 1}` : ''}</span>
                                )}
                            </div>
                        </div>
                        <div className="trending-card" onClick={() => history.push('/admin/photographers')}>
                            <img src="https://images.unsplash.com/photo-1537633552985-df8429e8048b?auto=format&fit=crop&q=80&w=400" alt="Photographers" className="trending-img" />
                            <div className="trending-title">
                                Best Photographers
                                {!selectedLocations.includes('All India') && (
                                    <span className="trending-loc-badge">{selectedLocations[0]}{selectedLocations.length > 1 ? ` +${selectedLocations.length - 1}` : ''}</span>
                                )}
                            </div>
                        </div>
                        <div className="trending-card" onClick={() => history.push('/marketplace?tab=rentals')}>
                            <img src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&q=80&w=400" alt="Decor" className="trending-img" />
                            <div className="trending-title">
                                Luxury Decor
                                {!selectedLocations.includes('All India') && (
                                    <span className="trending-loc-badge">{selectedLocations[0]}{selectedLocations.length > 1 ? ` +${selectedLocations.length - 1}` : ''}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Compact Benefit Cards */}
                <div className="mobile-benefits-section">
                    <h3 className="section-title-compact">✨ Why Choose ShadiGram?</h3>
                    <div className="mobile-carousel scroll-snap-carousel">
                        <div className="benefit-card-compact glass-panel">
                            <span className="benefit-icon">💎</span>
                            <h5>Multi Wedding</h5>
                            <p>Manage multiple clients' events in segregated spaces.</p>
                        </div>
                        <div className="benefit-card-compact glass-panel">
                            <span className="benefit-icon">📅</span>
                            <h5>Integrated Suite</h5>
                            <p>Synchronize check lists, guests, and payments live.</p>
                        </div>
                        <div className="benefit-card-compact glass-panel">
                            <span className="benefit-icon">📞</span>
                            <h5>Omnichannel RSVP</h5>
                            <p>Reach guests automatically over WhatsApp & IVR calls.</p>
                        </div>
                        <div className="benefit-card-compact glass-panel">
                            <span className="benefit-icon">📊</span>
                            <h5>Auto Budgets</h5>
                            <p>Real-time spend notifications and category calculators.</p>
                        </div>
                    </div>
                </div>

                {/* Premium Plan Details Section */}
                <div className="mobile-pricing-section">
                    <h3 className="section-title-compact">💰 Simple Celebration Plans</h3>
                    
                    <div className="mobile-annual-badge">
                        <span>🎉 Save 20% with Annual Plans</span>
                    </div>

                    <div className="mobile-carousel scroll-snap-carousel pricing-carousel">
                        {/* Basic Plan */}
                        <div className="pricing-card basic-card glass-panel">
                            <div className="pricing-card-header">
                                <span className="plan-icon">🌸</span>
                                <h4>Basic</h4>
                            </div>
                            <div className="price-container">
                                <span className="currency">₹</span>
                                <span className="amount">300</span>
                                <span className="period">/mo</span>
                            </div>
                            <ul className="plan-features">
                                <li>✦ 10 GB Storage</li>
                                <li>✦ 100 Guest Invitations</li>
                                <li>✦ Standard Support</li>
                            </ul>
                            <button className="plan-btn basic-btn" onClick={() => history.push('/pricing')}>
                                Get Started
                            </button>
                        </div>

                        {/* Silver Plan */}
                        <div className="pricing-card silver-card popular glass-panel">
                            <div className="popular-badge">Most Popular</div>
                            <div className="pricing-card-header">
                                <span className="plan-icon">💍</span>
                                <h4>Silver</h4>
                            </div>
                            <div className="price-container">
                                <span className="currency">₹</span>
                                <span className="amount">999</span>
                                <span className="period">/mo</span>
                            </div>
                            <ul className="plan-features">
                                <li>✦ 100 GB Storage</li>
                                <li>✦ 250 Guest Invitations</li>
                                <li>✦ RSVP Tracking</li>
                                <li>✦ Priority Support</li>
                            </ul>
                            <button className="plan-btn silver-btn" onClick={() => history.push('/pricing')}>
                                Choose Silver
                            </button>
                        </div>

                        {/* Gold Plan */}
                        <div className="pricing-card gold-card dark-panel">
                            <div className="pricing-card-header">
                                <span className="plan-icon">👑</span>
                                <h4>Gold</h4>
                            </div>
                            <div className="price-container">
                                <span className="currency">₹</span>
                                <span className="amount">1,999</span>
                                <span className="period">/mo</span>
                            </div>
                            <ul className="plan-features">
                                <li>✦ Unlimited Storage</li>
                                <li>✦ Unlimited Guests</li>
                                <li>✦ AI RSVP Agent</li>
                                <li>✦ Custom Domain</li>
                            </ul>
                            <button className="plan-btn gold-btn" onClick={() => history.push('/pricing')}>
                                Go Gold ✨
                            </button>
                        </div>
                    </div>

                    <div className="pricing-preview-footer">
                        <button className="see-all-plans-btn" onClick={() => history.push('/pricing')}>
                            See Full Plan Details →
                        </button>
                        <p className="pricing-guarantee-note">
                            🔒 Secure via UPI & Razorpay · 7-day money-back guarantee
                        </p>
                    </div>
                </div>

                {/* Mobile Footer */}
                <footer className="mobile-footer">
                    <img src="/shadigram-logo-bold.png" alt="Shadi Gram logo" className="mobile-footer-logo" />
                    <p>&copy; {new Date().getFullYear()} Shadi Gram — Shadi Wali App.</p>
                </footer>
            </div>
            {/* Floating WhatsApp Action Button */}
            <a
                href="https://wa.me/917999782728?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20get%20free%20trial%20credentials."
                target="_blank"
                rel="noopener noreferrer"
                className="floating-whatsapp"
                title="Chat on WhatsApp for Free Trial"
            >
                <svg viewBox="0 0 448 512" fill="white"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg>
            </a>

            <InviteAuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
            />
        </div>
    );
};

export default LandingPage;
